"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChecklistFieldLogic = void 0;
const cloneDeep_1 = __importDefault(require("lodash-es/cloneDeep"));
const differenceWith_1 = __importDefault(require("lodash-es/differenceWith"));
const react_fast_compare_1 = __importDefault(require("react-fast-compare"));
const field_logic_1 = require("../../field-type-registry/field-logic");
const comparisons_1 = require("../../comparisons/comparisons");
const google_sheet_1 = require("../../utils/google-sheet");
const checklist_fieldtype_1 = require("../../safe-field-value/text-fields/checklist-field/checklist.fieldtype");
const texts_1 = require("../../texts");
const intl_1 = require("../../locale/intl");
const validation_1 = require("../../validation/validation");
const checklist_1 = require("../../utils/checklist");
const get_value_1 = require("../../safe-field-value/text-fields/checklist-field/get-value");
class ChecklistFieldLogic extends field_logic_1.BaseFieldTypeLogic {
    constructor() {
        super(...arguments);
        this.name = "checklistfield";
        this.icon = "checklist";
        this.iconColor = "#6A849B";
        this.label = (0, intl_1.$t)(texts_1.checklistFieldText.checklist);
        this.placeholder = (0, intl_1.$t)(texts_1.checklistFieldText.search_by_item_content);
        this.searchSynonyms = [
            (0, intl_1.$t)(texts_1.checklistFieldText.list),
            (0, intl_1.$t)(texts_1.checklistFieldText.todo),
        ];
        this.groupByMode = "groupBy";
        this.singleExportEligible = true;
        // @ts-expect-error: Need to resolve
        this.comparisons = {
            is_empty: comparisons_1.isZero,
            is_not_empty: comparisons_1.isGreatThanZero,
            all_items_resolved: comparisons_1.isZero,
            not_all_items_resolved: comparisons_1.isGreatThanZero,
            percent_complete_greater_than: comparisons_1.isGreaterThan,
            percent_complete_less_than: comparisons_1.isLessThan,
        };
        this.fieldValueGetter = new checklist_fieldtype_1.ChecklistFieldValueGetter();
    }
    get validators() {
        return { required: validation_1.checklistRequired, maxItemLength: validation_1.maxItemLength, maxItems: validation_1.maxItems };
    }
    getInlineEditRules(field) {
        return {
            ...super.getInlineEditRules(field),
            editorWidth: "100%",
            saveKeys: ["Tab"],
            canDeleteValue: false,
            editOnStartTyping: false,
            inputControlEditMode: "OnModalOpen",
            cellPresenceEventMode: "OnModalOpen",
            allowAddRecordToGroup: false,
        };
    }
    valueToString(value) {
        if (!value) {
            return "";
        }
        if (typeof value === "string") {
            return value;
        }
        const { completed_items, total_items, items } = value;
        const itemsString = items === null || items === void 0 ? void 0 : items.map((item) => item.content.html).join("\n");
        if (itemsString) {
            return `${(0, checklist_1.getChecklistPercentageLabel)(value)} ${completed_items}/${total_items}\n${itemsString}`;
        }
        return `${(0, checklist_1.getChecklistPercentageLabel)(value)} ${completed_items}/${total_items}`;
    }
    getGroupLabel(fieldValue) {
        return (0, checklist_1.getChecklistPercentageLabel)(fieldValue);
    }
    valueToExport(value) {
        const totalItems = (value === null || value === void 0 ? void 0 : value.total_items) || 0;
        const completedItems = (value === null || value === void 0 ? void 0 : value.completed_items) || 0;
        return totalItems ? `${completedItems}/${totalItems}` : "";
    }
    valueToGoogleSheet(fieldValue, _field, _userPreferences, _record) {
        const value = this.valueToExport(fieldValue);
        return (0, google_sheet_1.makeRowDataValues)(value, "stringValue");
    }
    sortKey(value, _field) {
        if (!(value === null || value === void 0 ? void 0 : value.total_items)) {
            // empty checklists should be sorted first
            return "-1";
        }
        return (0, checklist_1.getChecklistPercentageLabel)(value);
    }
    groupByKey(value, field) {
        if (!(value === null || value === void 0 ? void 0 : value.total_items)) {
            // empty checklists should be gruped as "Empty" instead of "0%"
            return "";
        }
        return this.sortKey(value, field);
    }
    processFieldValue(record, slug, comparison, field) {
        var _a, _b, _c;
        const fieldValue = super.processFieldValue(record, slug, comparison, field);
        const total_items = (_a = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.total_items) !== null && _a !== void 0 ? _a : 0;
        if (["all_items_resolved", "not_all_items_resolved"].includes(comparison)) {
            const completed_items = (_b = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.completed_items) !== null && _b !== void 0 ? _b : 0;
            return total_items > 0 ? total_items - completed_items : -1;
        }
        if (["percent_complete_greater_than", "percent_complete_less_than"].includes(comparison)) {
            const completed_items = (_c = fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.completed_items) !== null && _c !== void 0 ? _c : 0;
            return total_items > 0 ? (completed_items / total_items) * 100 : 0;
        }
        return total_items;
    }
    filterValueToString(filterValue, field, userPreferences, comparison) {
        var _a;
        if (["all_items_resolved", "not_all_items_resolved"].includes(comparison)) {
            return "";
        }
        if (["percent_complete_greater_than", "percent_complete_less_than"].includes(comparison)) {
            return (_a = filterValue === null || filterValue === void 0 ? void 0 : filterValue.toString()) !== null && _a !== void 0 ? _a : "";
        }
        return super.filterValueToString(filterValue, field, userPreferences, comparison);
    }
    isValueEmpty(fieldValue) {
        return !(fieldValue === null || fieldValue === void 0 ? void 0 : fieldValue.total_items);
    }
    getEmptyValue() {
        return (0, get_value_1.getEmptyChecklistValue)();
    }
    isDefaultEmpty(field) {
        var _a;
        return !((_a = this.getDefaultValue(field)) === null || _a === void 0 ? void 0 : _a.total_items);
    }
    formatActivityRecordChangeValues(previousValue, currentValue) {
        var _a, _b;
        if (this.isValueEmpty(previousValue) || this.isValueEmpty(currentValue)) {
            return { previousValue, currentValue };
        }
        const removedValues = {
            ...previousValue,
            items: (0, differenceWith_1.default)(previousValue.items, (_a = currentValue.items) !== null && _a !== void 0 ? _a : [], react_fast_compare_1.default),
        };
        const addedValues = {
            ...currentValue,
            items: (0, differenceWith_1.default)(currentValue.items, (_b = previousValue.items) !== null && _b !== void 0 ? _b : [], react_fast_compare_1.default),
        };
        return { previousValue: removedValues, currentValue: addedValues };
    }
    isValueEqual(fieldValue1, fieldValue2) {
        var _a, _b;
        // clone is necessary here to avoid 'valueOf is not a function' errors when using react-fast-compare `isEqual`
        const items1 = (_a = (0, cloneDeep_1.default)(fieldValue1 === null || fieldValue1 === void 0 ? void 0 : fieldValue1.items)) !== null && _a !== void 0 ? _a : [];
        const items2 = (_b = (0, cloneDeep_1.default)(fieldValue2 === null || fieldValue2 === void 0 ? void 0 : fieldValue2.items)) !== null && _b !== void 0 ? _b : [];
        return ((fieldValue1 === null || fieldValue1 === void 0 ? void 0 : fieldValue1.total_items) === (fieldValue2 === null || fieldValue2 === void 0 ? void 0 : fieldValue2.total_items) &&
            (fieldValue1 === null || fieldValue1 === void 0 ? void 0 : fieldValue1.completed_items) === (fieldValue2 === null || fieldValue2 === void 0 ? void 0 : fieldValue2.completed_items) &&
            items1.length === items2.length &&
            items1.every((item, index) => {
                var _a, _b;
                const item2 = items2 === null || items2 === void 0 ? void 0 : items2[index];
                return ((item === null || item === void 0 ? void 0 : item.assignee) === (item2 === null || item2 === void 0 ? void 0 : item2.assignee) &&
                    (item === null || item === void 0 ? void 0 : item.completed) === (item2 === null || item2 === void 0 ? void 0 : item2.completed) &&
                    (item === null || item === void 0 ? void 0 : item.due_date) === (item2 === null || item2 === void 0 ? void 0 : item2.due_date) &&
                    (0, react_fast_compare_1.default)((_a = item === null || item === void 0 ? void 0 : item.content) === null || _a === void 0 ? void 0 : _a.data, (_b = item2 === null || item2 === void 0 ? void 0 : item2.content) === null || _b === void 0 ? void 0 : _b.data));
            }));
    }
    isFieldExpandableOnRecordModal() {
        return true;
    }
    isFullValueLoadNeeded(fieldValue) {
        var _a;
        return (!fieldValue || (fieldValue.total_items > 0 && !((_a = fieldValue.items) === null || _a === void 0 ? void 0 : _a.length)));
    }
    isPartialValue(fieldValue) {
        return !!fieldValue && !("items" in fieldValue);
    }
    canChangeValue(_field, fromValue, toValue) {
        return this.isValueEqual(fromValue, toValue);
    }
    getLinkedRecordFieldSmartWidth(_field) {
        return {
            defaultWidht: 90,
            minWidth: 45,
        };
    }
    prepareValueForClipboard(value) {
        if (!value) {
            return { text: "", rawValue: value };
        }
        if (typeof value === "string") {
            return { text: value, rawValue: null };
        }
        const { completed_items, total_items } = value;
        const text = `${(0, checklist_1.getChecklistPercentageLabel)(value)} ${completed_items}/${total_items}`;
        return { text, rawValue: value };
    }
    isSupportedByBulkAction() {
        return false;
    }
}
exports.ChecklistFieldLogic = ChecklistFieldLogic;
